<div class="container">
  <br/>
  <br/>

  <h1>Terms of Service</h1>
  <br/>

  <p>
    Welcome to Croissant! We are a monthly subscription service that gives our
    members access to the best workspaces in their city. The Croissant website
    (the “Site”) is operated by Croissant Inc. (“Croissant”). Please read
    carefully the following Terms of Use, which may be updated by Croissant from
    time to time without notice to you (the Terms of Use, as modified from time
    to time, the “Terms”). The most current version of the Terms can be accessed
    at any time at https://www.getcroissant.com/tos.
  </p>

  <h3>ACCEPTANCE OF TERMS OF USE</h3>

  <p>
    By accessing, browsing, and/or using the Site, you accept and agree to be
    bound to these Terms. In addition, when using particular Croissant services,
    you also shall be subject to any posted guidelines, FAQs, or rules
    applicable to such services, which may be posted and modified from time to
    time, including our <a routerLink="/privacy">Privacy Policy</a> and, for
    business and commercial accounts, our
    <a routerLink="/dpa">Data Processing Agreement</a> ("DPA").
  </p>

  <h3>MEMBERSHIP</h3>

  <p>
    <b>Eligible Hours.</b> Members receive hours of workspace time each
    membership cycle (“Eligible Hours”) to be used to visit any workspace of
    their choice available on our platform. For Luminary memberships, this
    amounts to 120 total hours across all venues, during each such membership
    cycle. For Creator memberships, this amounts to 40 total hours across all
    venues, during each such membership cycle. For Explorer memberships, this
    amounts to 10 total hours across all venues, during each such membership
    cycle. Members cannot transfer or gift hours to third parties, including
    other Croissant members. Croissant reserves the right to increase from time
    to time in its sole discretion the number of Eligible Hours a member can
    take in a given cycle (such as pursuant to certain promotions that we may
    offer from time to time). Croissant makes no guarantee on the availability
    of hours as access to workspaces is on a space-available basis.
  </p>

  <p>
    <b>Guest Hours.</b> Members also receive hours of time each membership cycle
    for guests (“Guest Hours”) to be used to invite friends or colleagues
    ("guests") to join them at participating workspaces on our platform. For
    Luminary memberships, this amounts to 20 total hours across all venues,
    during each such membership cycle. For Creator memberships, this amounts to
    5 total hours across all venues, during each such membership cycle. For
    Explorer memberships, this amounts to 0 total hours across all venues,
    during each such membership cycle. Any usage over this amount of hours is
    billed at a rate applicable to the geography of the membership. For example,
    a member based in New York City will be billed at a rate of $7 per hour,
    while a member who is based in London would be billed £4.50 per hour.
    Current rates are indicated on the web page, and in the mobile application,
    where guests are added. The member will be automatically charged that amount
    using their valid Payment Method within 48 hours of such usage. The member
    will be charged in increments of 30 minutes. For example, if the member used
    1 hour and 14 minutes of guest hours in one coworking session, they will be
    charged for an hour and a half of guest hours. Members cannot transfer or
    gift Guest Hours to third parties, including other Croissant members.
    Croissant reserves the right to increase from time to time in its sole
    discretion the number of Guest Hours a member can take in a given cycle
    (such as pursuant to certain promotions that we may offer from time to
    time). Croissant makes no guarantee on the availability of seating for
    guests as access to workspaces is on a space-available basis.
  </p>

  <p>
    <b>Rollover Hours.</b> For members who signed up for the Creator or Luminary
    plan on the Croissant website on or after April 1st, 2017, signed up using
    the Croissant mobile application (version 2.7.8 or later) on or after May
    15th, 2017, or signed up for the Explorer plan on or after September 14th,
    2017, any unused Eligible Hours and Guest Hours will roll over after that
    month is over ("Rollover Hours"). Members who did not sign up for one of
    these plans, or did not sign up during this time frame, do not automatically
    have Rollover Hours enabled for their membership plan, unless they contact
    our support team and elect switch to a membership plan that is eligible for
    Rollover Hours. Rollover Hours are available for up to 6 months from the
    date they are first rolled over, and any usage of the hours is first
    consumed from the current cycle's hours, and then from the second most
    recent cycle, and so on. Rollover Hours will carry over even if the
    membership plan is upgraded or downgraded, as long as the member maintains
    an active subscription of the Explorer, Creator, or Luminary plans. Rollover
    hours are not eligible for being rolled over onto the Pay As You Go plan,
    and are not eligible for being rolled over if the eligible subscription to
    the Explorer, Creator, or Luminary plan is cancelled. Please note that any
    Rollover Hours not used on your account at the time of cancellation are
    forfeited, and will not be exercisable if the membership is reactivated
    again in the future.
  </p>

  <p>
    <b>Additional Hours.</b> Members also can use additional hours ("Additional
    Hours") as needed if they have already used all of their allotted hours on
    their current plan by enabling overage in their account settings or by
    contacting our support team. Additional Hours are billed at a rate
    applicable to the geography of the membership. For example, a member based
    in New York City will be billed at a rate of $7 per hour, while a member who
    is based in London would be billed £4.50 per hour. Current rates are
    indicated in the account settings, on the page where overage can be enabled.
    The member will be automatically charged that amount using their valid
    Payment Method within 48 hours of such usage.
  </p>

  <h3>MEMBERSHIP CYCLE</h3>

  <p>
    Your Croissant membership starts on the date that you sign up for a
    subscription and submit payment via a valid Payment Method. Each Croissant
    cycle is one month in length, and will automatically renew each month. For
    example, if you purchase your Croissant membership on April 5, your
    membership will automatically renew on May 5 (see “Billing Cycles,” below).
    Once your new month starts, you will receive your full number of Eligible
    Hours for such new monthly cycle. Please note that any Eligible Hours not
    used during a monthly membership cycle are forfeited, and will not be
    exercisable in the next subscription cycle, except in the case of Rollover
    Hours (see “Rollover Hours” above).
  </p>

  <p>
    You must provide us with a current, valid, accepted method of payment (as
    such may be updated from time to time, “Payment Method”) to use Croissant.
    We automatically will bill the monthly membership fee to your Payment Method
    each month until your Croissant is cancelled or terminated.
  </p>

  <h3>BILLING</h3>

  <p>
    <b>Recurring Billing.</b> By starting your Croissant membership, you
    authorize us to charge you a monthly membership fee at the then current
    rate.
  </p>

  <p>
    <b>Billing Cycle.</b> When you sign up and purchase your Croissant, your
    first subscription cycle will be billed immediately. Your subscription will
    auto-renew and you will be billed on the same date each month. We reserve
    the right to change the timing of our billing, in particular, as indicated
    below, if your Payment Method has not successfully settled. In the event
    your paying membership began on a day not contained in a given month, we may
    bill your Payment Method on a day in the applicable month or such other day
    as we deem appropriate. For example, if you started your Croissant
    membership or became a paying member on January 31st, your next payment date
    is likely to be February 28th, and your Payment Method would be billed on
    that date. Your renewal date may change due to changes in your Membership.
    You are responsible for paying your renewal fee if you do not cancel your
    membership with appropriate notice.
  </p>

  <p>
    <b>No Refunds.</b> PAYMENTS ARE NONREFUNDABLE AND THERE ARE NO REFUNDS OR
    CREDITS FOR PARTIALLY USED PERIODS. Following any cancellation, however, you
    will continue to have access to the service through the end of your current
    billing period. At any time, and for any reason, we may provide a refund,
    discount, or other consideration to some or all of our members (“credits”).
    The amount and form of such credits, and the decision to provide them, are
    at our sole and absolute discretion. The provision of credits in one
    instance does not entitle you to credits in the future for similar
    instances, nor does it obligate us to provide credits in the future, under
    any circumstance.
  </p>

  <p>
    <b>Payment Methods.</b> You may edit your Payment Method information in your
    account settings. If a payment is not successfully settled, due to
    expiration, insufficient funds, or otherwise, and you do not edit your
    Payment Method information or cancel your account (see “Cancellation”
    below), you remain responsible for any uncollected amounts and authorize us
    to continue billing the Payment Method, as it may be updated. This may
    result in a change to your payment billing dates.
  </p>

  <p>
    <b>Cancellation.</b> You may terminate your monthly subscription at any time
    by clicking on the cancel link in the account settings under the Membership
    tab. However, your first month cannot be cancelled and is non-refundable.
    For example, if your next billing date is September 25th, you need to cancel
    by September 24th in order to avoid being charged for the next month. Note
    that if you do terminate your membership, you will have to pay the regular
    rate for a membership if you want to return to the Croissant in future
    months, as if you were a new member.
  </p>

  <p>
    <b>Travel Policy.</b> To accommodate occasional travel, your Croissant
    membership may permit you to go to workspaces outside of the home geography
    associated with your membership. If you use more than fifty percent (50%) of
    your hours in your first month or (ii) any given two (2)-month period, in
    each case in a geography outside of your home geography, then upon notice
    communicated to you through email or such other means we may deem
    appropriate, we reserve the right to change your membership to such other
    geography and charge you the applicable recurring membership rate. You
    understand and agree that we may transition your membership in this way.
  </p>

  <p>
    <b>Trials.</b> From time to time we may offer a trial membership that
    includes standard access to the Croissant platform during the trial period.
    Unless otherwise communicated, a trial begins at the moment of sign up and
    ends at 11:00pm ET on the last day of the trial (for a one-week trial, this
    would be the same weekday of following week). Each trial membership
    automatically will convert to a regular monthly membership and price unless
    canceled by 11:00pm ET on the day before the last day of trial. Customers
    that cancel and do not convert to a regular membership may not go to
    workspaces after the end of the trial membership period (even if the held a
    seat before the end of the applicable trial period). Unless we expressly
    communicate otherwise, trial memberships are only available to new customers
    that have never had a Croissant account before, are not transferable, may
    not be combined with other offers or redeemed for cash and are void where
    prohibited.
  </p>

  <h3>OTHER FEES</h3>

  <p>
    <b>Amenities.</b> Your Croissant membership covers your access to hours of
    work at open format coworking desk space at venues on the Croissant
    platform. Some of our venues may also charge equipment or other amenity
    fees. The Croissant only gives you access to the regular, open format
    coworking desk space for which you signed up. The venue may have additional
    fees for use of additional amenities or spaces, such as conference rooms.
  </p>

  <p>
    <b>Conference rooms.</b> Any fees which the we may charge you for the use of
    conference rooms will be as set out on our website or mobile application at
    the time of purchase, are inclusive of any taxes payable, are due
    immediately and are non-refundable. This no refund policy shall apply at all
    times regardless of your decision to terminate your usage, our decision to
    terminate your usage, disruption caused to our website, mobile application
    or venues, either planned, accidental or intentional, or any reason
    whatsoever. We reserve the right to determine final prevailing pricing. You
    do hereby agree that you shall be charged fees based on the length of time
    of your booking of the conference rooms (regardless if you only make use of
    the conference rooms for less than your booked time). If you exceed the
    amount of time for which you booked a conference room, then you shall be
    charged for such excess time, as well as an overage charge if such excess
    time conflicts with another member's booked time. You do hereby consent to
    such excess time charges and to such overage charge. At our sole discretion,
    we may make promotional offers with different features and different rates
    to any of our customers. These promotional offers, unless made to you, shall
    have no bearing whatsoever on your offer or contract.
  </p>

  <h3>TERMINATION BY CROISSANT</h3>

  <p>
    You agree that Croissant, in its sole discretion and for any or no reason,
    may terminate any account (or any part thereof) you may have with Croissant
    or use of the Site and remove and discard all or any part of your account or
    any content uploaded by you, at any time. Croissant may also in its sole
    discretion and at any time discontinue providing access to the Site, or any
    part thereof, with or without notice. This includes discontining access at
    one or more venues on Croissant. You agree that any termination of your
    access to the Site or any account you may have or portion thereof may be
    effected without prior notice, and you agree that Croissant will not be
    liable to you or any third party for any such termination. Croissant does
    not permit copyright infringing activities on the Site and reserves the
    right to terminate access to the Site and remove all content submitted by
    any persons who are found to be repeat infringers. Any suspected fraudulent,
    abusive, or illegal activity that may be grounds for termination of your use
    of the Site may be referred to appropriate law enforcement authorities.
    These remedies are in addition to any other remedies Croissant may have at
    law or in equity.
  </p>

  <h3>ELIGIBILITY; REGISTRATION INFORMATION AND PASSWORD; SITE ACCESS</h3>

  <p>
    THE SITE IS NOT AVAILABLE TO ANY
    USERS SUSPENDED OR REMOVED FROM THE SITE BY CROISSANT. BY USING THE SITE,
    YOU REPRESENT THAT YOU HAVE NOT BEEN
    PREVIOUSLY SUSPENDED OR REMOVED FROM THE SITE.
  </p>

  <p>
    If you are using or opening an account on the Site on behalf of a company,
    entity, or organization (a “Subscribing Organization”), then you represent
    and warrant that you are an authorized representative of that Subscribing
    Organization with the authority to bind such organization to these Terms;
    and agree to be bound by these Terms on behalf of such Subscribing
    Organization.
  </p>

  <p>
    You agree that the information you provide to Croissant on registration and
    at all other times will be true, accurate, current, and complete. You also
    agree that you will ensure that this information is kept accurate and
    up-to-date at all times. When you register, either you will be asked to
    create a password or a temporary password will automatically be generated
    for you and emailed to you. You are solely responsible for maintaining the
    confidentiality of your account and password and for restricting access to
    your computer, and you agree to accept responsibility for all activities
    that occur under your account.
  </p>

  <h3>PRIVACY</h3>

  <p>
    Your privacy is important to Croissant. The Croissant Privacy Policy is
    hereby incorporated into these Terms by reference. Please read the privacy
    policy carefully for information relating to Croissant’s collection, use,
    and disclosure of your personal information.
  </p>

  <h3>PROHIBITED CONDUCT</h3>

  <p>
    By using the Site, you promise not to:
  </p>

  <p>
    Harass, threaten, or defraud Site users;<br/>
    Make unsolicited offers, advertisements, proposals, or send junk mail to
    users;<br/>
    Impersonate another person or access another user’s account without that
    person’s permission;<br/>
    Share Croissant-issued passwords with any third party or encourage any other
    user to do so;<br/>
    Permit third parties to use any hours booked under your own membership,
    including other members;<br/>
    Misrepresent the source, identity, or content of information transmitted via
    the Site, including deleting the copyright or other proprietary rights;<br/>
    Upload material (e.g. virus) that is damaging to computer systems or data of
    Croissant or users of the Site;<br/>
    Upload copyrighted material that is not your own or that you do not have the
    legal right to distribute, display, and otherwise make available to others;
    or<br/>
    Upload or send to Site users pornographic, threatening, embarrassing,
    hateful, racially or ethnically insulting, libelous, or otherwise
    inappropriate content.
  </p>

  <h3>PROHIBITED USES</h3>

  <p>
    This site is intended for U.S. residents only. Those who choose to access
    the Site do so at their own initiative and are responsible for compliance
    with all local rules including, without limitation, rules about the
    internet, data, email, or privacy.
  </p>

  <p>
    As a condition of your use of the Site, you will not use the Site for any
    purpose that is unlawful or prohibited by these Terms. You may not use the
    Site in any manner that, in our sole discretion, could damage, disable,
    overburden, or impair it or interfere with any other party’s use and
    enjoyment of the Site. You may not attempt to gain unauthorized access to
    the Site, or any part of the Site, other accounts, computer systems or
    networks connected to the Site, or any part of them, through hacking,
    password mining, or any other means or interfere or attempt to interfere
    with the proper working of the Site or any activities conducted on the Site.
    You may not remove, circumvent, disable, damage or otherwise interfere with
    security-related features of the Site, any features that prevent or restrict
    use or copying of any content accessible through the Site, or any features
    that enforce limitations on the use of the Site or the content therein. You
    may not obtain or attempt to obtain any materials or information through any
    means not intentionally made available through the Site. You agree neither
    to modify the Site in any manner or form, nor to use modified versions of
    the Site, including (without limitation) for the purpose of obtaining
    unauthorized access to the Site.
  </p>

  <p>
    The Site may contain robot exclusion headers. You agree that you will not
    use any robot, spider, scraper, or other automated means to access the Site
    for any purpose without our express written permission or bypass our robot
    exclusion headers or other measures we may use to prevent or restrict access
    to the Site.
  </p>

  <p>
    You much be 18 years of age or older to use this Site or to purchase a
    Croissant membership. Croissant reserves the right to refuse service,
    terminate accounts, remove or edit content, or cancel orders in their sole
    discretion.
  </p>

  <h3>USER SUBMISSIONS</h3>
  <p>
    <b>General.</b> The Site provides certain features which enable you and
    other users to submit, post, and share content with other users, which may
    include without limitation text, graphic and pictorial works, or any other
    content submitted by you and other users through the Site (“User
    Submissions”). User Submissions are displayed for informational purposes
    only and are not controlled by Croissant. Croissant cannot guarantee any
    anonymity or confidentiality with respect to any User Submissions, and
    strongly recommends that you think carefully about what you upload to the
    Site. You understand that all User Submissions are the sole responsibility
    of the person from whom such User Submission originated. This means that
    you, and not Croissant, are entirely responsible for all User Submissions
    that you upload, post, e-mail, transmit, or otherwise make available through
    the Site.
  </p>

  <p>
    <b>Right to Remove or Edit User Submissions.</b> Croissant makes no
    representations that it will publish or make available on the Site any User
    Submissions, and reserves the right, in its sole discretion, to refuse to
    allow any User Submissions on the Site, or to edit or remove any User
    Submission at any time with or without notice. Without limiting the
    generality of the preceding sentence, Croissant complies with the Digital
    Millennium Copyright Act, and will remove User Submissions upon receipt of a
    compliant takedown notice (see the “Digital Millennium Copyright Act”
    section below).
  </p>

  <p>
    <b>License Grant by You to Croissant.</b> You retain all your ownership
    rights in original aspects of your User Submissions. By submitting User
    Submissions to Croissant, you hereby grant Croissant and its affiliates,
    sublicensees, partners, designees, and assignees of the Site (collectively,
    the “Croissant Licensees”) a worldwide, non-exclusive, fully paid-up,
    royalty-free, perpetual, irrevocable, sublicensable, and transferable
    license to use, reproduce (including by making mechanical reproductions),
    distribute, modify, adapt, prepare derivative works of, publicly display,
    publicly perform, and otherwise exploit your User Submissions and
    derivatives thereof in connection with the Site and Croissant’s (and its
    successors’) business, including, without limitation, for marketing,
    promoting, and redistributing part or all of the Site (and derivative works
    thereof), in any media formats and through any media channels now known or
    hereafter discovered or developed.
  </p>

  <p>
    <b>User Submissions Representations and Warranties.</b> You are solely
    responsible for your own User Submissions and the consequences of posting or
    publishing them. In connection with User Submissions, you affirm, represent,
    and warrant that: (i) you own, or have the necessary licenses, rights,
    consents, and permissions to use and authorize Croissant to use all patent,
    trademark, copyright, or other proprietary rights in and to your User
    Submissions to enable inclusion and use of your User Submissions in the
    manner contemplated by Croissant and these Terms, and to grant the rights
    and license set forth above, and (ii) your User Submissions, Croissant’s or
    any Croissant Licensee’s use of such User Submissions pursuant to these
    Terms, and Croissant’s or any of Croissant Licensee’s exercise of the
    license rights set forth above, do not and will not: (a) infringe, violate,
    or misappropriate any third-party right, including any copyright, trademark,
    patent, trade secret, moral right, privacy right, right of publicity, or any
    other intellectual property or proprietary right; (b) cause injury to any
    other person; (c) violate these Terms or any applicable law or regulation;
    or (d) require obtaining a license from or paying fees or royalties to any
    third party for the exercise of any rights granted in these Terms,
    including, by way of example and not limitation, the payment of any
    royalties to any copyright owners, including any royalties to any agency,
    collection society, or other entity that administers such rights on behalf
    of others. Croissant may, but is not obligated to, monitor and edit or
    remove any activity or content, including but not limited to content that
    Croissant determines in its sole discretion to be violative of the standards
    of this Site. Croissant takes no responsibility and assumes no liability for
    any User Submissions.
  </p>

  <p>
    <b>Inaccurate or Offensive User Submissions.</b> You understand that when
    using the Site, you may be exposed to User Submissions from a variety of
    sources and that Croissant does not endorse and is not responsible for the
    accuracy, usefulness, safety, or intellectual property rights of or relating
    to such User Submissions. You further understand and acknowledge that you
    may be exposed to User Submissions that are inaccurate, offensive, indecent,
    or objectionable. YOU AGREE TO WAIVE, AND HEREBY DO WAIVE, ANY LEGAL OR
    EQUITABLE RIGHTS OR REMEDIES YOU HAVE OR MAY HAVE AGAINST CROISSANT WITH
    RESPECT THERETO.
  </p>

  <p>
    <b>Feedback.</b> If you provide Croissant with any comments, bug reports,
    feedback, or modifications proposed or suggested by you to the Site
    (“Feedback”), Croissant shall have the right to use such Feedback at its
    discretion, including, but not limited to the incorporation of such
    suggested changes into the Site. You hereby grant Croissant a perpetual,
    irrevocable, nonexclusive license under all rights necessary to incorporate
    and use your Feedback for any purpose.
  </p>

  <h3>ADDITIONAL TERMS</h3>

  <p>
    When using the Site, you will be subject to any additional posted guidelines
    or rules applicable to specific products, services, or features which may be
    posted from time to time (the “FAQs”). All such FAQs are hereby incorporated
    by reference into the Terms.
  </p>

  <h3>OWNERSHIP; PROPRIETARY RIGHTS</h3>

  <p>
    The Site is owned and operated by Croissant. The visual interfaces,
    graphics, design, compilation, information, computer code, products,
    software (including any downloadable software), services, and all other
    elements of the Site provided by Croissant (“Materials”) are protected by
    United States copyright, trade dress, patent, and trademark laws,
    international conventions, and all other relevant intellectual property and
    proprietary rights, and applicable laws. Except for any content uploaded by
    you, all Materials contained on the Site are the copyrighted property of
    Croissant or its subsidiaries or affiliated companies and/or third-party
    licensors. All trademarks, service marks, and trade names are proprietary to
    Croissant or its affiliates and/or third-party licensors. Except as
    expressly authorized by Croissant, you agree not to sell, license,
    distribute, copy, modify, publicly perform or display, transmit, publish,
    edit, adapt, create derivative works from, or otherwise make unauthorized
    use of the Materials.
  </p>

  <h3>THIRD-PARTY SITES, PRODUCTS AND SERVICES; LINKS</h3>

  <p>
    The Site may include links to other web sites or services (“Linked Sites”)
    solely as a convenience to users. Croissant does not endorse any such Linked
    Sites or the information, material, products, or services contained on other
    linked sites or accessible through other Linked Sites. Furthermore,
    Croissant makes no express or implied warranties with regard to the
    information, material, products, or services that are contained on or
    accessible through linked sites. ACCESS AND USE OF LINKED SITES, INCLUDING
    THE INFORMATION, MATERIAL, PRODUCTS, AND SERVICES ON LINKED SITES OR
    AVAILABLE THROUGH LINKED SITES, IS SOLELY AT YOUR OWN RISK.
  </p>

  <p>
    Your correspondence or business dealings with, or participation in
    promotions of, advertisers found on or through the Site are solely between
    you and such advertiser. YOU AGREE THAT CROISSANT WILL NOT BE RESPONSIBLE OR
    LIABLE FOR ANY LOSS OR DAMAGE OF ANY SORT INCURRED AS THE RESULT OF ANY SUCH
    DEALINGS OR AS THE RESULT OF THE PRESENCE OF SUCH ADVERTISERS ON THE SITE.
  </p>

  <h3>NOTICE</h3>

  <p>
    Except as explicitly stated otherwise, legal notices will be served, with
    respect to Croissant, on Croissant’s national registered agent, and, with
    respect to you, to the email address you provide to Croissant during the
    registration process. Notice will be deemed given 24 hours after email is
    sent, unless the sending party is notified that the email address is
    invalid. Alternatively, we may give you legal notice by mail to the address
    provided during the registration process. In such case, notice will be
    deemed given three days after the date of mailing.
  </p>

  <h3>DISCLAIMERS; NO WARRANTIES</h3>

  <p>
    HOURS, ACTIVITIES, AND ANY OTHER SERVICES OFFERED VIA THE SITE ARE OFFERED
    AND PROVIDED BY THIRD PARTIES, NOT CROISSANT. YOU AGREE THAT ATTENDANCE AT
    OR USE OF ANY SUCH HOURS, ACTIVITIES, OR OTHER SERVICES IS SOLELY AT YOUR
    OWN RISK. IN NO EVENT SHALL CROISSANT BE LIABLE FOR ANY INJURY, LOSS, CLAIM,
    DAMAGE OR ANY SPECIAL, EXEMPLARY, PUNITIVE, INCIDENTAL OR CONSEQUENTIAL
    DAMAGES OF ANY KIND, WHETHER BASED IN CONTRACT, TORT OR OTHERWISE, WHICH
    ARISES OUT OF OR IS ANY WAY CONNECTED WITH A USER’S ATTENDANCE OR
    PARTICIPATION IN A WORKSPACE, SERVICE OR APPOINTMENT MADE THROUGH THE SITE,
    OR THE PERFORMANCE OR NON-PERFORMANCE OF ANY PROVIDER IN CONNECTION WITH THE
    SERVICES. IN ADDITION, ALL USERS SPECIFICALLY UNDERSTAND AND AGREE THAT ANY
    THIRD PARTY DIRECTING USERS TO THE SITE BY REFERRAL, LINK OR ANY OTHER MEANS
    IS NOT LIABLE TO THE USER FOR ANY REASON WHATSOEVER, INCLUDING BUT NOT
    LIMITED TO DAMAGES OR LOSS ASSOCIATED WITH THE USE OF THE SERVICES, THE SITE
    OR ANY CONTENT ON THE SITE. CROISSANT IS NEITHER AN AGENT OF NOR IS
    CONNECTED WITH ANY PROVIDER WITH WHICH A USER HAS MADE A RESERVATION, OTHER
    THAN AS THE PROVIDERS OF THE SERVICES PROVIDED ON THE SITE. WITHOUT LIMITING
    ANYTHING HEREIN, CROISSANT DISCLAIMS ALL WARRANTIES, WHETHER STATUTORY,
    EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF
    MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE, RELATED TO ANY HOURS,
    ACTIVITIES, OR OTHER SERVICES OFFERED VIA THE SITE.
  </p>

  <p>
    UNLESS OTHERWISE EXPRESSLY STATED BY CROISSANT, THE SITE AND ANY
    DOWNLOADABLE SOFTWARE, CONTENT, SERVICES, OR APPLICATIONS MADE AVAILABLE IN
    CONJUNCTION WITH OR THROUGH THE SITE ARE PROVIDED “AS IS” AND “AS AVAILABLE”
    WITHOUT WARRANTIES OF ANY KIND EITHER EXPRESS OR IMPLIED. TO THE FULLEST
    EXTENT PERMISSIBLE PURSUANT TO APPLICABLE LAW, CROISSANT, ITS SUPPLIERS AND
    PARTNERS DISCLAIM ALL WARRANTIES, STATUTORY, EXPRESS OR IMPLIED, INCLUDING,
    BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
    PARTICULAR PURPOSE, AND NON-INFRINGEMENT OF PROPRIETARY RIGHTS.
  </p>

  <p>
    UNLESS OTHERWISE EXPRESSLY STATED BY CROISSANT, CROISSANT, ITS SUPPLIERS AND
    PARTNERS DO NOT WARRANT OR MAKE ANY REPRESENTATIONS (I) THAT THE SITE AND
    ANY DOWNLOADABLE SOFTWARE, CONTENT, SERVICES, OR APPLICATIONS MADE AVAILABLE
    IN CONJUNCTION WITH OR THROUGH THE SITE WILL BE UNINTERRUPTED OR ERROR-FREE,
    THAT DEFECTS WILL BE CORRECTED, OR THAT THE SITE AND ANY DOWNLOADABLE
    SOFTWARE, CONTENT, SERVICES, OR APPLICATIONS MADE AVAILABLE IN CONJUNCTION
    WITH OR THROUGH THE SITE OR THE SERVER THAT MAKES THEM AVAILABLE ARE FREE OF
    VIRUSES OR OTHER HARMFUL COMPONENTS, OR (II) REGARDING THE USE OF THE SITE
    AND ANY DOWNLOADABLE SOFTWARE, CONTENT, SERVICES, OR APPLICATIONS MADE
    AVAILABLE IN CONJUNCTION WITH OR THROUGH THE SITE IN TERMS OF CORRECTNESS,
    ACCURACY, RELIABILITY, OR OTHERWISE. YOU UNDERSTAND AND AGREE THAT YOU
    DOWNLOAD OR OTHERWISE OBTAIN MATERIAL OR DATA THROUGH THE USE OF THE SITE AT
    YOUR OWN DISCRETION AND RISK AND THAT YOU WILL BE SOLELY RESPONSIBLE FOR ANY
    DAMAGES TO YOUR COMPUTER SYSTEM OR LOSS OF DATA THAT RESULTS FROM THE
    DOWNLOAD OF SUCH MATERIAL OR DATA.
  </p>

  <p>
    CERTAIN STATE LAWS DO NOT ALLOW LIMITATIONS ON IMPLIED WARRANTIES OR THE
    EXCLUSION OR LIMITATION OF CERTAIN DAMAGES. IF THESE LAWS APPLY TO YOU, SOME
    OR ALL OF THE ABOVE DISCLAIMERS, EXCLUSIONS, OR LIMITATIONS MAY NOT APPLY TO
    YOU, AND YOU MIGHT HAVE ADDITIONAL RIGHTS.
  </p>

  <h3>INDEMNIFICATION; HOLD HARMLESS</h3>

  <p>
    You agree to indemnify and hold Croissant, its affiliated companies, and its
    suppliers and partners harmless from any claims, losses, damages,
    liabilities, including attorney’s fees, arising out of your use or misuse of
    the Site, violation of these Terms, violation of the rights of any other
    person or entity, or any breach of the foregoing representations,
    warranties, and covenants.
  </p>

  <h3>WAIVER</h3>

  <p>
    A provision of these Terms may be waived only by a written instrument
    executed by the party entitled to the benefit of such provision. The failure
    of any party at any time to require performance of any provision of these
    Terms will in no manner affect such party’s right at a later time to enforce
    the same. A waiver of any breach of any provision of these Terms will not be
    construed as a continuing waiver of other breaches of the same or other
    provisions of these Terms.
  </p>

  <h3>LIMITATION OF LIABILITY AND DAMAGES</h3>

  <p>
    UNDER NO CIRCUMSTANCES, INCLUDING, BUT NOT LIMITED TO, NEGLIGENCE, WILL
    CROISSANT OR ITS AFFILIATES, CONTRACTORS, EMPLOYEES, AGENTS, OR THIRD-PARTY
    PARTNERS OR SUPPLIERS BE LIABLE FOR ANY SPECIAL, INDIRECT, INCIDENTAL,
    CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES ARISING FROM OR RELATED TO THE
    USE OF THE SITE OR RELATED TO THE USE OF ANY SPACES OR SERVICES PROVIDED BY
    THIRD-PARTY PARTNERS, INCLUDING THOSE THAT RESULT FROM THE USE OR THE
    INABILITY TO USE THE MATERIALS ON THE SITE OR THE INABILITY TO USE THE
    MATERIALS OF ANY SPACES OR SERVICES PROVIDED BY THIRD-PARTY PARTNERS, OR ANY
    OTHER INTERACTIONS WITH CROISSANT, EVEN IF CROISSANT OR A CROISSANT
    AUTHORIZED REPRESENTATIVE HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH
    DAMAGES. APPLICABLE LAW MAY NOT ALLOW THE LIMITATION OR EXCLUSION OF
    LIABILITY OR INCIDENTAL OR CONSEQUENTIAL DAMAGES, SO THE ABOVE LIMITATION OR
    EXCLUSION MAY NOT APPLY TO YOU. IN SUCH CASES, CROISSANT’S LIABILITY WILL BE
    LIMITED TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW.
  </p>

  <p>
    IN NO EVENT WILL CROISSANT OR ITS AFFILIATES, CONTRACTORS, EMPLOYEES,
    AGENTS, OR THIRD-PARTY PARTNERS OR SUPPLIERS HAVE ANY LIABILITY TO YOU FOR
    DAMAGES, LOSSES, AND CAUSES OF ACTION ARISING OUT OF OR RELATING TO THESE
    TERMS OR YOUR USE OF THE SITE (WHETHER IN CONTRACT, TORT INCLUDING
    NEGLIGENCE, WARRANTY, OR OTHERWISE).
  </p>

  <h3>INFRINGEMENT POLICY</h3>

  <p>
    Please see our Digital Millennium Copyright Act document for an explanation
    of our copyright and trademark policies.
  </p>

  <h3>DISPUTE RESOLUTION</h3>

  <p>
    <b>General.</b> Generally, if a dispute arises between Croissant and you,
    our goal is to provide you with a neutral and cost effective means of
    resolving the dispute quickly. Accordingly, you and Croissant agree that the
    parties will resolve any claim or controversy at law or equity that arises
    out of this Agreement or the Site (a “Claim”) in accordance with one of the
    subsections below or as you and we otherwise agree in writing. Before
    resorting to these alternatives, we strongly encourage you to first contact
    us directly to seek a resolution. We will consider reasonable requests to
    resolve the dispute through alternative dispute resolution procedures, such
    as mediation or arbitration, as alternatives to litigation.
  </p>

  <p>
    <b>Choice of Law; Forum.</b> These Terms shall be governed in all respects
    by the laws of the State of New York, without regard to conflict of law
    provisions. You agree that any claim or dispute you may have against
    Croissant must be resolved by a court located in New York County, New York,
    except as otherwise agreed by the parties or as described in the Arbitration
    Option paragraph below. You agree to submit to the personal jurisdiction of
    the courts located within New York County, New York for the purpose of
    litigating all such claims or disputes.
  </p>

  <p>
    <b>Arbitration Option.</b> For any claim (excluding claims for injunctive or
    other equitable relief) where the total amount of the award sought is less
    than $10,000, the party requesting relief may elect to resolve the dispute
    in a cost effective manner through binding, non-appearance-based
    arbitration. In the event a party elects arbitration, they shall initiate
    such arbitration through an established alternative dispute resolution
    (“ADR”) provider mutually agreed upon by the parties. The ADR provider and
    the parties must comply with the following rules: (i) the arbitration shall
    be conducted by telephone, online and/or be solely based on written
    submissions, the specific manner shall be chosen by the party initiating the
    arbitration; (ii) the arbitration shall not require any personal appearance
    by the parties or witnesses unless otherwise mutually agreed by the parties;
    and (iii) any judgment on the award rendered by the arbitrator may be
    entered in any court of competent jurisdiction.
  </p>

  <h3>MISCELLANEOUS</h3>

  <p>
    <b>Severability.</b> If any provision of these Terms shall be unlawful,
    void, or for any reason unenforceable, then that provision will be deemed
    severable from these Terms and will not affect the validity and
    enforceability of any remaining provisions.
  </p>

  <p>
    <b>Headings.</b> The heading references herein are for convenience purposes
    only, do not constitute a part of these Terms, and will not be deemed to
    limit or affect any of the provisions hereof.
  </p>

  <p>
    <b>Entire Agreement.</b> This is the entire agreement between you and
    Croissant relating to the subject matter herein and will not be modified
    except in writing, signed by both parties, or by a change to these Terms or
    Guidelines made by Croissant as set forth in the Preamble to these Terms.
  </p>

  <p>
    <b>Claims; Statute of Limitations.</b> YOU AND CROISSANT AGREE THAT ANY
    CAUSE OF ACTION ARISING OUT OF OR RELATED TO THESE TERMS OR THE SITE MUST
    COMMENCE WITHIN ONE (1) YEAR AFTER THE CAUSE OF ACTION ACCRUES. OTHERWISE,
    SUCH CAUSE OF ACTION IS PERMANENTLY BARRED.
  </p>

  <p>
    <b>Disclosures.</b> The services hereunder are offered by Croissant Inc.,
    located at: One World Trade Center, Suite 8500, New York, NY 10007 and
    email: support&#64;getcroissant.com. If you are a California resident, you may
    have this same information emailed to you by sending a letter to the
    foregoing address with your email address and a request for this
    information.
  </p>

  <br/>
  <br/>

</div>
